// Hostname-based routing helpers for the TinyPDFs tools suite.
//
// Production layout:
//   tinypdfs.com           -> main marketing site (unchanged)
//   tools.tinypdfs.com     -> PDF Tools dashboard
//   <slug>.tinypdfs.com    -> individual tool (e.g. pdfsplitter.tinypdfs.com)
//
// Dev overrides:
//   ?host=tools             -> simulate tools.tinypdfs.com
//   ?host=<slug>            -> simulate <slug>.tinypdfs.com
//   ?tool=<slug>            -> alias for host=<slug>
// These are parsed once from window.location on load.

import { isKnownToolSlug } from '../tools/registry';

const ROOT_DOMAINS = ['tinypdfs.com'];
const TOOLS_DASHBOARD_SUBDOMAIN = 'tools';

function parseOverride() {
  if (typeof window === 'undefined') return null;
  try {
    const params = new URLSearchParams(window.location.search || '');
    const host = params.get('host');
    const tool = params.get('tool');
    if (host) return String(host).toLowerCase();
    if (tool) return String(tool).toLowerCase();
  } catch {
    // ignore
  }
  return null;
}

function extractSubdomain(hostname) {
  if (!hostname) return null;
  const h = hostname.toLowerCase().replace(/^www\./, '');

  for (const root of ROOT_DOMAINS) {
    if (h === root) return null; // apex
    if (h.endsWith(`.${root}`)) {
      const sub = h.slice(0, -1 * (root.length + 1));
      // Only consider the left-most label as the subdomain.
      const first = sub.split('.')[0];
      return first || null;
    }
  }
  return null;
}

// Returns one of:
//   { kind: 'main' }                           — render the normal site
//   { kind: 'dashboard' }                      — render the tools dashboard
//   { kind: 'tool', slug: 'pdfsplitter' }      — render a specific tool
export function resolveSiteContext() {
  if (typeof window === 'undefined') return { kind: 'main' };

  const override = parseOverride();
  if (override) {
    if (override === TOOLS_DASHBOARD_SUBDOMAIN) return { kind: 'dashboard' };
    if (isKnownToolSlug(override)) return { kind: 'tool', slug: override };
  }

  const sub = extractSubdomain(window.location.hostname);
  if (!sub) return { kind: 'main' };
  if (sub === TOOLS_DASHBOARD_SUBDOMAIN) return { kind: 'dashboard' };
  if (isKnownToolSlug(sub)) return { kind: 'tool', slug: sub };

  return { kind: 'main' };
}

// Build a URL for a tool. Always path-based so all SEO weight is consolidated
// on tinypdfs.com and internal links stay on a single origin. The subdomain
// detection in resolveSiteContext() above still handles requests that arrive
// via <slug>.tinypdfs.com in case subdomains are reintroduced later.
export function buildToolUrl(slug) {
  return `/tools/${slug}`;
}

export function buildDashboardUrl() {
  return `/tools`;
}

function normalizeCanonicalPath(pathname) {
  const value = String(pathname || '/').trim();
  if (!value || value === '/') return '/';
  return `/${value.replace(/^\/+/, '')}`;
}

// Kept pure so build tooling and tests can validate canonical behaviour
// without depending on the browser's window.location implementation.
export function getCanonicalUrlForHost(hostname, pathname = '/') {
  const host = String(hostname || '').toLowerCase().replace(/:\d+$/, '');
  const matchedRoot = ROOT_DOMAINS.find((r) => host === r || host.endsWith(`.${r}`));
  const canonicalRoot = matchedRoot || ROOT_DOMAINS[0];

  // If we were rendered via a legacy tool subdomain, canonicalize to the
  // matching path on the apex.
  const sub = matchedRoot ? extractSubdomain(host) : null;
  if (sub === TOOLS_DASHBOARD_SUBDOMAIN) {
    return `https://${canonicalRoot}/tools`;
  }
  if (sub && isKnownToolSlug(sub)) {
    return `https://${canonicalRoot}/tools/${sub}`;
  }

  // Preview hosts (including the local prerender server) must still emit the
  // production canonical instead of retaining the homepage tag from the SPA
  // shell. Canonicals always use HTTPS regardless of the serving protocol.
  return `https://${canonicalRoot}${normalizeCanonicalPath(pathname)}`;
}

// Return the canonical URL for the current page, used to emit
// <link rel="canonical">. Canonicals always point at the primary domain so
// any accidental duplicate (e.g. a preview host or a legacy subdomain) gets
// consolidated back onto tinypdfs.com.
export function getCanonicalUrl(pathname = '/') {
  if (typeof window === 'undefined') return null;
  return getCanonicalUrlForHost(window.location.hostname, pathname);
}
